import { Injectable } from '@angular/core';
import { Observable, forkJoin, of } from 'rxjs';
import { map, catchError } from 'rxjs/operators';
import moment from 'moment';
import { ScoreEventsService } from 'src/app/shared/services/data-api/score-events.service';
import {
  TopEventsRequestParams,
  BullBearTopEventsRequestParams,
} from 'src/app/shared/models/score-events.interface';
import { ScoreUtilsService } from 'src/app/shared/utils/score-utils.service';
import { TopEventsParamsService } from 'src/app/shared/utils/top-events-params.service';
import { DateUtils } from 'src/app/shared/utils/date.utils';
import { EventMention } from './chat-charts-events.models';

@Injectable({
  providedIn: 'root',
})
export class ChatChartsEventsDataService {
  constructor(
    private scoreEventsService: ScoreEventsService,
    private topEventsParams: TopEventsParamsService,
  ) {}

  fetchEventMentions(
    entity: string,
    scoreType: string,
    timeframe: string,
  ): Observable<EventMention[]> {
    const scoreKey = ScoreUtilsService.resolveScoreKey(scoreType);
    const isBullBear = TopEventsParamsService.isBullBearScoreKey(scoreKey);
    const isSentiment = scoreType.toLowerCase() === 'sentiment';
    const time = DateUtils.fromTo(timeframe);
    const comparisonTime = this.computeComparisonTimeframe(time);

    let fetchFn: (t: {
      dateFrom: string;
      dateTo: string;
      period: number;
    }) => Observable<EventMention[]>;

    if (isSentiment) {
      fetchFn = (t) => this.fetchSentimentEvents(entity, t);
    } else if (isBullBear) {
      fetchFn = (t) =>
        this.fetchBullBearTopEvents(
          entity,
          this.topEventsParams.buildBullBearRequest({
            dateFrom: t.dateFrom,
            dateTo: t.dateTo,
            period: t.period,
          }),
        );
    } else {
      fetchFn = (t) =>
        this.fetchTopEvents(
          entity,
          this.topEventsParams.buildScoreLabRequest({
            scoreKey,
            dateFrom: t.dateFrom,
            dateTo: t.dateTo,
            period: t.period,
            topEventsSign: scoreKey === 'credit_watch' ? 'neg' : undefined,
          }),
        );
    }

    return forkJoin({
      current: fetchFn(time),
      comparison: fetchFn(comparisonTime),
    }).pipe(
      map(({ current, comparison }) =>
        this.mergeWithDelta(current, comparison),
      ),
      catchError(() => of([])),
    );
  }

  private fetchTopEvents(
    entity: string,
    params: TopEventsRequestParams,
  ): Observable<EventMention[]> {
    return this.scoreEventsService.getTopEvents(entity, 'company', params).pipe(
      map((response) =>
        (response.top_events || []).map((event) => ({
          id: event.id,
          name: event.name,
          type: event.type,
          contrib_amount: event.contrib_amount,
        })),
      ),
      catchError(() => of([])),
    );
  }

  private fetchSentimentEvents(
    entity: string,
    time: { dateFrom: string; dateTo: string; period: number },
  ): Observable<EventMention[]> {
    return this.scoreEventsService
      .getSentimentEvents(entity, 'company', {
        dateFrom: time.dateFrom,
        dateTo: time.dateTo,
      })
      .pipe(
        map((response) =>
          (response.events || []).map((item) => ({
            id: item.event.id,
            name: item.event.name,
            type: item.event.type,
            contrib_amount: item.count,
          })),
        ),
        catchError(() => of([])),
      );
  }

  private fetchBullBearTopEvents(
    entity: string,
    params: BullBearTopEventsRequestParams,
  ): Observable<EventMention[]> {
    return this.scoreEventsService
      .getBullBearTopEvents(entity, 'company', params)
      .pipe(
        map((response) =>
          (response.top_events || []).map((event) => ({
            id: event.id,
            name: event.name,
            type: event.type,
            contrib_amount: event.contrib_amount,
          })),
        ),
        catchError(() => of([])),
      );
  }

  private mergeWithDelta(
    current: EventMention[],
    comparison: EventMention[],
  ): EventMention[] {
    return current.map((event) => {
      const prevEvent = comparison.find((c) => c.id === event.id);
      const contrib_amount_prev = prevEvent?.contrib_amount ?? 0;
      return {
        ...event,
        contrib_amount_prev,
        delta: event.contrib_amount - contrib_amount_prev,
      };
    });
  }

  private computeComparisonTimeframe(currentTime: {
    dateFrom: string;
    dateTo: string;
    period: number;
  }): { dateFrom: string; dateTo: string; period: number } {
    const comparisonDateTo = currentTime.dateFrom;
    const comparisonDateFrom = moment(currentTime.dateFrom)
      .subtract(currentTime.period, 'days')
      .format('YYYY-MM-DD');

    return {
      dateFrom: comparisonDateFrom,
      dateTo: comparisonDateTo,
      period: currentTime.period,
    };
  }
}
